
/**
 * Rubato collection behaviour.
 *
 * 1. The in stock in my size toggle inside the Size filter group, which drives the
 *    Availability filter from Search & Discovery.
 * 2. Size and toggle persist in localStorage across collections and sessions.
 *
 * Load more lived here until round 61 part 13 and is gone: the grids paginate with real
 * ?page=n links now, so there is nothing to append and no scroll position to keep.
 */

const STORAGE_KEY = 'rubato:size-filter';

/* ------------------------------------------------------------------ */
/* In stock in my size, and remembering the size                       */
/* ------------------------------------------------------------------ */

function readStore() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function writeStore(value) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Private browsing, nothing to do
  }
}

/** The Availability filter's in stock input, if Search & Discovery renders it. */
function availabilityInput(root) {
  return root.querySelector('input[name*="filter.v.availability"][value="1"]');
}

function sizeInputs(root) {
  return [...root.querySelectorAll('input[name*="filter.v.option.size"]')];
}

function setUp(root) {
  const toggles = [...root.querySelectorAll('[data-rubato-instock]')];
  if (!toggles.length) return;

  for (const wrapper of toggles) {
    if (wrapper.dataset.rubatoWired === 'true') continue;
    const form = wrapper.closest('form') ?? document;
    const availability = availabilityInput(form);
    const input = wrapper.querySelector('[data-rubato-instock-input]');
    if (!availability || !input) continue;

    // The Availability group itself is replaced by this toggle
    availability.closest('.facets__panel')?.setAttribute('data-rubato-hidden', 'true');
    wrapper.dataset.rubatoWired = 'true';
    wrapper.removeAttribute('hidden');
    input.checked = availability.checked;

    input.addEventListener('change', () => {
      const store = readStore();
      store.inStock = input.checked;
      writeStore(store);

      availability.checked = input.checked;
      availability.dispatchEvent(new Event('change', { bubbles: true }));
    });

    for (const size of sizeInputs(form)) {
      size.addEventListener('change', () => {
        const chosen = sizeInputs(form)
          .filter((el) => el.checked)
          .map((el) => el.value);
        const store = readStore();
        store.sizes = chosen;

        // The toggle defaults to on the first time a size is picked
        if (chosen.length && store.inStock === undefined) {
          store.inStock = true;
          input.checked = true;
          availability.checked = true;
        }
        writeStore(store);
      });
    }
  }
}

/** Applies the remembered size on a collection that has no size filter in the URL yet. */
function applyRemembered(root) {
  const params = new URLSearchParams(window.location.search);
  for (const key of params.keys()) {
    if (key.includes('filter.v.option.size')) return;
  }

  const store = readStore();
  if (!store.sizes?.length) return;

  const form = root.querySelector('facets-form-component form') ?? root;
  const matching = sizeInputs(form).filter((input) => store.sizes.includes(input.value));
  if (!matching.length) return;

  for (const input of matching) input.checked = true;

  if (store.inStock) {
    const availability = availabilityInput(form);
    if (availability) availability.checked = true;
  }

  matching[matching.length - 1]?.dispatchEvent(new Event('change', { bubbles: true }));
}

function init() {
  const root = document.querySelector('.collection-wrapper') ?? document;
  setUp(root);
  applyRemembered(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// The facets form re-renders the section, so the toggle has to be wired up again.
let pending = false;
const rewire = () => {
  if (pending) return;
  pending = true;
  requestAnimationFrame(() => {
    pending = false;
    setUp(document);
  });
};

document.addEventListener('shopify:section:load', rewire);
new MutationObserver(rewire).observe(document.body, { childList: true, subtree: true });
